import { useState, useEffect } from 'react'
import { getApiUrl, fetchWithAuth } from '@/api'

interface TimeSlot {
  id: number
  start_time: string
  end_time: string
  max_count: number
  is_active: number
}

interface TimeSlotModalProps {
  onClose: () => void
}

const inputCls = 'w-full border border-[#e8e0dc] rounded-lg px-3 py-2 text-[13px] text-[#2a1f1a] outline-none focus:border-[#887267] transition-colors bg-white'

export default function TimeSlotModal({ onClose }: TimeSlotModalProps) {
  const [slots, setSlots] = useState<TimeSlot[]>([])
  const [loading, setLoading] = useState(true)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [newSlot, setNewSlot] = useState({ start_time: '', end_time: '', max_count: '1', is_active: 1 })
  const [editSlot, setEditSlot] = useState({ start_time: '', end_time: '', max_count: '1', is_active: 1 })
  const [saving, setSaving] = useState(false)
  const [deletingId, setDeletingId] = useState<number | null>(null)

  useEffect(() => {
    fetchSlots()
  }, [])

  const fetchSlots = async () => {
    setLoading(true)
    try {
      const res = await fetchWithAuth(getApiUrl('/marubit/api/counseling/slots'))
      if (!res.ok) throw new Error('조회 실패')
      const data: TimeSlot[] = await res.json()
      setSlots(data)
    } catch {
      alert('시간 슬롯을 불러오는 데 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }

  const handleAdd = async () => {
    if (!newSlot.start_time || !newSlot.end_time) { alert('시간을 입력해주세요.'); return }
    setSaving(true)
    try {
      const res = await fetchWithAuth(getApiUrl('/marubit/api/counseling/admin/slots'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          start_time: newSlot.start_time,
          end_time: newSlot.end_time,
          max_count: Number(newSlot.max_count),
          is_active: newSlot.is_active,
        }),
      })
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail ?? '등록 실패') }
      setNewSlot({ start_time: '', end_time: '', max_count: '1', is_active: 1 })
      setShowAddForm(false)
      fetchSlots()
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : '등록에 실패했습니다.')
    } finally {
      setSaving(false)
    }
  }

  const startEdit = (slot: TimeSlot) => {
    setEditingId(slot.id)
    setEditSlot({
      start_time: slot.start_time.slice(0, 5),
      end_time: slot.end_time.slice(0, 5),
      max_count: String(slot.max_count),
      is_active: slot.is_active,
    })
  }

  const handleUpdate = async (id: number) => {
    setSaving(true)
    try {
      const res = await fetchWithAuth(getApiUrl(`/marubit/api/counseling/admin/slots/${id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          start_time: editSlot.start_time,
          end_time: editSlot.end_time,
          max_count: Number(editSlot.max_count),
          is_active: editSlot.is_active,
        }),
      })
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail ?? '수정 실패') }
      setEditingId(null)
      fetchSlots()
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : '수정에 실패했습니다.')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: number) => {
    if (!window.confirm('이 시간 슬롯을 삭제하시겠습니까?')) return
    setDeletingId(id)
    try {
      const res = await fetchWithAuth(getApiUrl(`/marubit/api/counseling/admin/slots/${id}`), { method: 'DELETE' })
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail ?? '삭제 실패') }
      fetchSlots()
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : '삭제에 실패했습니다.')
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-[560px] max-h-[80vh] flex flex-col">

        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#f5f2f0]">
          <h2 className="text-[16px] font-bold text-[#2a1f1a]">상담시간 설정</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 bg-transparent border-none cursor-pointer text-[20px] leading-none"
          >×</button>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-gray-300 text-[13px]">불러오는 중...</div>
          ) : (
            <div className="flex flex-col gap-2">
              {slots.length === 0 && !showAddForm && (
                <p className="text-[13px] text-gray-300 text-center py-4">등록된 시간 슬롯이 없습니다.</p>
              )}
              {slots.map(slot => (
                <div key={slot.id} className="bg-[#fdf8f0] rounded-xl border border-[#e8e0dc] px-4 py-3">
                  {editingId === slot.id ? (
                    <div className="flex flex-col gap-3">
                      <div className="grid grid-cols-2 gap-2">
                        <div className="flex flex-col gap-1">
                          <label className="text-[11px] text-[#5a3d32] font-medium">시작 시간</label>
                          <input type="time" value={editSlot.start_time}
                            onChange={e => setEditSlot(p => ({ ...p, start_time: e.target.value }))}
                            className={inputCls} />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-[11px] text-[#5a3d32] font-medium">종료 시간</label>
                          <input type="time" value={editSlot.end_time}
                            onChange={e => setEditSlot(p => ({ ...p, end_time: e.target.value }))}
                            className={inputCls} />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-[11px] text-[#5a3d32] font-medium">최대 인원</label>
                          <input type="number" min={1} value={editSlot.max_count}
                            onChange={e => setEditSlot(p => ({ ...p, max_count: e.target.value }))}
                            className={inputCls} />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-[11px] text-[#5a3d32] font-medium">활성 여부</label>
                          <select value={editSlot.is_active}
                            onChange={e => setEditSlot(p => ({ ...p, is_active: Number(e.target.value) }))}
                            className={inputCls}>
                            <option value={1}>활성</option>
                            <option value={0}>비활성</option>
                          </select>
                        </div>
                      </div>
                      <div className="flex gap-2 justify-end">
                        <button onClick={() => setEditingId(null)}
                          className="text-[12px] text-gray-400 border border-[#e8e0dc] rounded-lg px-3 py-1.5 bg-white cursor-pointer hover:bg-[#f5f2f0] transition-colors">취소</button>
                        <button onClick={() => handleUpdate(slot.id)} disabled={saving}
                          className="text-[12px] text-white bg-[#887267] border-none rounded-lg px-3 py-1.5 cursor-pointer hover:bg-[#6b5248] transition-colors disabled:opacity-50">저장</button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-[13px] font-medium text-[#2a1f1a]">
                          {slot.start_time.slice(0, 5)} ~ {slot.end_time.slice(0, 5)}
                        </span>
                        <span className="text-[12px] text-gray-400">최대 {slot.max_count}명</span>
                        <span className={`text-[11px] px-2 py-0.5 rounded font-medium ${
                          slot.is_active ? 'bg-[#e8f5e9] text-[#388e3c]' : 'bg-[#f5f5f5] text-[#9e9e9e]'
                        }`}>
                          {slot.is_active ? '활성' : '비활성'}
                        </span>
                      </div>
                      <div className="flex gap-1.5">
                        <button onClick={() => startEdit(slot)}
                          className="text-[12px] text-[#887267] hover:text-[#6b5248] border border-[#e8d8ce] rounded px-2 py-1 bg-transparent cursor-pointer transition-colors">수정</button>
                        <button onClick={() => handleDelete(slot.id)} disabled={deletingId === slot.id}
                          className="text-[12px] text-red-400 hover:text-red-600 border border-red-100 rounded px-2 py-1 bg-transparent cursor-pointer transition-colors disabled:opacity-50">삭제</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* 추가 폼 */}
              {showAddForm && (
                <div className="bg-[#f5f2f0] rounded-xl border border-[#e8e0dc] px-4 py-3">
                  <p className="text-[12px] font-semibold text-[#5a3d32] mb-3">새 시간 슬롯 추가</p>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div className="flex flex-col gap-1">
                      <label className="text-[11px] text-[#5a3d32] font-medium">시작 시간</label>
                      <input type="time" value={newSlot.start_time}
                        onChange={e => setNewSlot(p => ({ ...p, start_time: e.target.value }))}
                        className={inputCls} />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[11px] text-[#5a3d32] font-medium">종료 시간</label>
                      <input type="time" value={newSlot.end_time}
                        onChange={e => setNewSlot(p => ({ ...p, end_time: e.target.value }))}
                        className={inputCls} />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[11px] text-[#5a3d32] font-medium">최대 인원</label>
                      <input type="number" min={1} value={newSlot.max_count}
                        onChange={e => setNewSlot(p => ({ ...p, max_count: e.target.value }))}
                        className={inputCls} />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[11px] text-[#5a3d32] font-medium">활성 여부</label>
                      <select value={newSlot.is_active}
                        onChange={e => setNewSlot(p => ({ ...p, is_active: Number(e.target.value) }))}
                        className={inputCls}>
                        <option value={1}>활성</option>
                        <option value={0}>비활성</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => setShowAddForm(false)}
                      className="text-[12px] text-gray-400 border border-[#e8e0dc] rounded-lg px-3 py-1.5 bg-white cursor-pointer hover:bg-[#f5f2f0] transition-colors">취소</button>
                    <button onClick={handleAdd} disabled={saving}
                      className="text-[12px] text-white bg-[#887267] border-none rounded-lg px-3 py-1.5 cursor-pointer hover:bg-[#6b5248] transition-colors disabled:opacity-50">추가</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 border-t border-[#f5f2f0] flex justify-between">
          <button
            onClick={() => setShowAddForm(true)}
            disabled={showAddForm}
            className="text-[13px] text-[#887267] font-semibold px-4 py-2 rounded-lg border border-[#e8d8ce] bg-white cursor-pointer hover:bg-[#f5f2f0] transition-colors disabled:opacity-50"
          >+ 시간 추가</button>
          <button
            onClick={onClose}
            className="text-[13px] text-white bg-[#887267] font-semibold px-5 py-2 rounded-lg border-none cursor-pointer hover:bg-[#6b5248] transition-colors"
          >닫기</button>
        </div>
      </div>
    </div>
  )
}